import { effectiveSuggestionHtml } from '@seen/core/models/suggestionStagingBasic'
import { cn } from '@seen/ui'
import { IconArrowBackUp, IconCheck, IconX } from '@tabler/icons-react'
import { EditorContent, type UseEditorOptions, useEditor } from '@tiptap/react'
import {
  type JSX,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { tiptapVariants } from '#src/components/core/tiptapVariants.ts'
import { useIslandActions } from '#src/components/rich-text-editor/IslandActionsContext.tsx'
import {
  createIslandKeymap,
  type IslandKeymapHandlers,
} from '#src/components/rich-text-editor/islandKeymap.ts'
import { ISLAND_PORTAL_SELECTOR } from '#src/components/rich-text-editor/islandPortalFocus.ts'
import type { IslandRegistry } from '#src/components/rich-text-editor/islandRegistry.ts'
import type { IslandExitSide } from '#src/components/rich-text-editor/islandTraversal.ts'
import {
  getIslandEditorExtensions,
  seedIslandContent,
} from '#src/components/rich-text-editor/suggestionContentExtensions.ts'
import { useDebouncedCallback } from '#src/hooks/useDebouncedCallback.ts'

export interface IslandEditorProps {
  /* Identity for the registry and for every host action the toolbar invokes.
   * A suggestion id in the note editor; a questionnaire item's linkId on the
   * care plan — the island itself never needs to know which. */
  id: string
  // The AI's untouched proposal HTML: what Restore original reverts to.
  originalHtml: string
  // A prior island edit of this suggestion, if one was kept.
  editedContent?: string
  registry: IslandRegistry
  /* Where the click that opened this island landed, as a position in the
   * PREVIEW's document. Preview and island parse the same seed HTML, but the
   * island schema drops node types it does not admit, so on a suggestion
   * carrying one the position is an approximation — Tiptap clamps it into the
   * island document's bounds. Absent → caret at the end, the pre-click default. */
  initialCaretPos?: number
}

// editedContent is committed on a debounce while the island is open, and flushed
// once on close — so a touched suggestion survives an AI rerun without a write
// per keystroke.
const EDITED_CONTENT_DEBOUNCE_MS = 500

/* The island (design §2.a/2.b): a standalone, NON-collaborative Tiptap editor
 * mounted inside a pending suggestion's widget, seeded from the AI op content and
 * edited in place before accept. Its editor has its OWN document and no
 * Collaboration/Yjs binding, so edits are local by construction and never enter
 * the shared doc until accept — that locality is the whole privacy guarantee.
 *
 * Host-owned mount: the widget shell renders this through the host editor's
 * ReactRenderer, so the island's node views and the @-mention picker portal into
 * the host React tree and inherit its providers (CurrentParticipantContext,
 * etc.) — the read-only context bridge the picker needs, reused, not rebuilt.
 *
 * Toolbar (2.b): Accept with edits / Restore original / Reject, driven through
 * IslandActionsContext (the island has no direct access to the host editor,
 * accept pipeline, or persistence). A bare island — one with no actions, so
 * nothing was ever proposed for the text it holds — renders no toolbar and no
 * tint at all: it is ordinary inline text editing, left by clicking away or by
 * tabbing out, and there is no decision for a control to offer. */
export function IslandEditor({
  id,
  originalHtml,
  editedContent,
  registry,
  initialCaretPos,
}: IslandEditorProps): JSX.Element {
  const actions = useIslandActions()

  // What the island OPENS on: a re-opened island must show the clinician their
  // own edit — the content an accept would insert — not the AI original they
  // already edited away. Distinct from `originalHtml`, which Restore original
  // and the registry's getOriginalContent still mean literally.
  const seedHtml = useMemo(
    () => effectiveSuggestionHtml({ editedContent }, originalHtml),
    [editedContent, originalHtml]
  )

  /* Assigned further down, once the flush/close helpers the handlers call
   * exist. The keymap reads through this ref on each press so ONE extension
   * instance covers the island's whole life — an extension identity that
   * changed per render would rebuild the editor and drop the draft. */
  const keymapHandlersRef = useRef<IslandKeymapHandlers>({
    onUndoBeyondHistory: () => false,
    onRedoBeyondHistory: () => false,
    onExit: () => false,
  })
  const islandKeymap = useMemo(
    () =>
      createIslandKeymap({
        onUndoBeyondHistory: () =>
          keymapHandlersRef.current.onUndoBeyondHistory(),
        onRedoBeyondHistory: () =>
          keymapHandlersRef.current.onRedoBeyondHistory(),
        onExit: (side) => keymapHandlersRef.current.onExit(side),
      }),
    []
  )

  const options = useMemo(
    () =>
      ({
        extensions: [...getIslandEditorExtensions(), islandKeymap],
        // Seed is parsed through the island schema, which drops node types it
        // does not admit. Mentions are carried through verbatim, so rewording a
        // proposal never costs it the citations it arrived with.
        content: seedIslandContent(seedHtml),
        editable: true,
        // Read once, at editor construction — a later prop change cannot move a
        // caret the clinician has since placed themselves.
        autofocus: initialCaretPos ?? 'end',
        parseOptions: {
          // ##PreserveWhitespace Mirror the host editor / preview so adjacent
          // inline nodes keep their spaces.
          preserveWhitespace: true,
        },
        editorProps: {
          // On a proposal the island card already signals edit mode (tint +
          // toolbar), and on a bare island the text is meant to read exactly as
          // it did unopened, so the contenteditable's own focus outline is
          // redundant either way — suppressed the same way CollaborativeEditor
          // suppresses its own.
          attributes: {
            class: cn(
              tiptapVariants({ noPlaceholder: true }),
              'focus-visible:outline-hidden'
            ),
          },
        },
      }) satisfies UseEditorOptions,
    [seedHtml, initialCaretPos, islandKeymap]
  )

  const editor = useEditor(options)

  // dirty = the clinician actually edited since mount/restore; gates the on-close
  // flush so a no-op or restored island never re-touches the record. decided =
  // accepted/rejected via the toolbar; suppresses the flush entirely (the record
  // is already resolved).
  const dirtyRef = useRef(false)
  const decidedRef = useRef(false)

  const persistEdited = actions?.persistEdited
  const debouncedPersist = useDebouncedCallback((id: string, html: string) => {
    persistEdited?.(id, html)
  }, EDITED_CONTENT_DEBOUNCE_MS)

  /* Synchronous flush of the in-progress draft to editedContent (invariant 6),
   * held in a ref so the registered controller always flushes with the CURRENT
   * editor/actions/dirty state without re-registering on every render. Called by
   * the conflict/whole-doc-replace reconciler (registry.flushOpenIslands) before
   * it repositions or drops proposals — a reposition keeps the island open, so
   * its on-close flush never fires and a pending debounced write would otherwise
   * be lost. Self-guarding and idempotent: writes only a dirty, undecided draft,
   * cancels the pending debounce to avoid a duplicate write, and clears dirty so
   * a second flush (or a later on-close flush) is a no-op. */
  const flushEditedRef = useRef<() => void>(() => {})
  flushEditedRef.current = (): void => {
    if (!editor || !actions || !dirtyRef.current || decidedRef.current) {
      return
    }
    debouncedPersist.cancel()
    actions.persistEdited(id, editor.getHTML())
    dirtyRef.current = false
  }

  /* Cmd+Z past the island's own history: hand the surface to the host rather
   * than run two histories side by side. The draft is flushed FIRST — the host
   * undo can be the whole-doc replace that drops this suggestion's anchor, and
   * a drop tears the island down through a path that is not this component's
   * own close. The island then closes only if the host actually undid
   * something, so a press with nothing left anywhere is a swallowed no-op that
   * leaves the clinician where they were. Undecided throughout: the suggestion
   * stays pending with the edit preserved, exactly as on a click-outside. */
  const forwardHistory = (direction: 'undo' | 'redo'): boolean => {
    if (!actions) {
      return false
    }
    flushEditedRef.current()
    const ran = direction === 'undo' ? actions.hostUndo() : actions.hostRedo()
    if (ran) {
      registry.close(id)
    }
    return ran
  }

  const exitToHost = (side: IslandExitSide): boolean => {
    if (!actions?.exitToHost(id, side)) {
      return false
    }
    // The host now has focus, so the island's own focusout would close it
    // anyway; closing here makes the exit identical whether or not the browser
    // reports a relatedTarget, and registry.close is idempotent.
    registry.close(id)
    return true
  }

  keymapHandlersRef.current = {
    onUndoBeyondHistory: () => forwardHistory('undo'),
    onRedoBeyondHistory: () => forwardHistory('redo'),
    onExit: exitToHost,
  }

  // Register the island's controller so accept reads its current content (and
  // Restore its original) directly — the island's live edit exists only as this
  // local editor's node state. Cleared on unmount (i.e. when the island closes).
  useEffect(() => {
    if (!editor) {
      return
    }
    registry.registerController(id, {
      getCurrentContent: () => editor.getHTML(),
      getOriginalContent: () => originalHtml,
      close: () => registry.close(id),
      flushEdited: () => flushEditedRef.current(),
    })
    return () => registry.unregisterController(id)
  }, [editor, registry, id, originalHtml])

  // Persist editedContent: debounced while open, flushed once on close (design
  // §2.b). No-op without host actions (bare editor).
  useEffect(() => {
    if (!editor || !actions) {
      return
    }
    const onUpdate = (): void => {
      dirtyRef.current = true
      debouncedPersist(id, editor.getHTML())
    }
    editor.on('update', onUpdate)
    return () => {
      editor.off('update', onUpdate)
      debouncedPersist.cancel()
      if (dirtyRef.current && !decidedRef.current) {
        actions.persistEdited(id, editor.getHTML())
      }
    }
  }, [editor, actions, id, debouncedPersist])

  /* Whether there is anything for Restore original to restore: the island's
   * live document differs from the AI original. Compared as doc JSON with both
   * sides through the same island-schema parse, so formatting
   * noise in the source HTML can't fake a difference. Live rather than
   * editedContent-derived — the clinician's unflushed typing (inside the 500ms
   * persist debounce) already diverges the doc, and a restore converges it
   * again, with no extra wiring: setContent fires the same `update` this
   * listens to. Disabled-not-hidden so the toolbar's width never jumps under a
   * clinician who is mid-typing when the button becomes meaningful. */
  const originalDocJson = useMemo(
    () => JSON.stringify(seedIslandContent(originalHtml)),
    [originalHtml]
  )
  const [canRestore, setCanRestore] = useState(false)
  useEffect(() => {
    if (!editor) {
      return
    }
    const recompute = (): void => {
      setCanRestore(
        JSON.stringify(editor.state.doc.toJSON()) !== originalDocJson
      )
    }
    recompute()
    editor.on('update', recompute)
    return () => {
      editor.off('update', recompute)
    }
  }, [editor, originalDocJson])

  const restore = useCallback((): void => {
    if (!editor) {
      return
    }
    // Local revert to the AI's original, and clear the persisted edit so the
    // record stops counting as touched. setContent fires an update that would
    // re-mark dirty, so reset the flag AFTER and cancel the scheduled write.
    editor.commands.setContent(seedIslandContent(originalHtml))
    debouncedPersist.cancel()
    dirtyRef.current = false
    actions?.clearEdited(id)
  }, [editor, originalHtml, debouncedPersist, actions, id])

  const acceptWithEdits = useCallback((): void => {
    if (!editor || !actions) {
      return
    }
    decidedRef.current = true
    // Pass the live island content straight to the pipeline (the persisted
    // editedContent write is async and not readable back in time).
    void actions.acceptWithEdits(id, editor.getHTML())
    registry.close(id)
  }, [editor, actions, id, registry])

  const reject = useCallback((): void => {
    decidedRef.current = true
    actions?.reject(id)
    registry.close(id)
  }, [actions, id, registry])

  /* Dismiss (leaving the island): closes it WITHOUT deciding — the unmount
   * cleanup above flushes a dirty draft to editedContent, so the suggestion
   * stays pending with the edit preserved. The island reads as ordinary text
   * you move into and out of, not as a modal, so it carries neither a close
   * button nor an Escape binding; the two triggers below (a pointerdown
   * outside, and focus leaving for another element) are both just "the
   * clinician went somewhere else", and both route through here so the flush
   * and the undecided state are identical either way. A click outside fires
   * both — harmless, because registry.close is idempotent (the second call
   * finds the id already gone and emits nothing) and the flush happens once, in
   * the single unmount.
   *
   * Escape in particular is bound app-wide to back-navigation (useBackHotkey in
   * App.tsx), on document in the CAPTURE phase — the island could never have
   * suppressed it from a React bubble handler anyway, and claiming it would
   * only add a second, invisible effect to a keypress that is already leaving
   * the route. The unmount flush covers that navigation: on an in-app (SPA)
   * back the draft is persisted just as it is on a click-outside close. */
  const dismiss = useCallback((): void => {
    registry.close(id)
  }, [registry, id])

  const wrapperRef = useRef<HTMLDivElement>(null)

  /* Both dismissal triggers exempt the same three things: anything inside the
   * island (its own toolbar is not "elsewhere"); the accept surfaces (hover
   * popover, status bar), which read the OPEN island's live content off its
   * controller, so unmounting it as they are activated would destroy the very
   * text they are about to accept; and the island's own portalled UI — the
   * @-picker popover, which Radix renders under document.body even though the
   * island's editor is what opened it, so a containment test on the wrapper
   * alone reads it as the clinician leaving. The two exemptions are separate
   * attributes because they are separate reasons, not one general
   * do-not-dismiss flag. */
  const staysOpenFor = useCallback((element: Element): boolean => {
    return (
      wrapperRef.current !== null &&
      (wrapperRef.current.contains(element) ||
        element.closest('[data-ai-suggestion-surface]') !== null ||
        element.closest(ISLAND_PORTAL_SELECTOR) !== null)
    )
  }, [])

  useEffect(() => {
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target
      if (!(target instanceof Element) || wrapperRef.current === null) {
        return
      }
      if (staysOpenFor(target)) {
        return
      }
      dismiss()
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [dismiss, staysOpenFor])

  /* Focus leaving the island dismisses it too, so a keyboard user tabbing out
   * gets the same close-and-flush as a click outside. Native listener on the
   * wrapper rather than a React onBlur: the widget's DOM is detached from the
   * document (below) at exactly the moment this matters, and React's delegated
   * events do not fire from a detached subtree.
   *
   * A null relatedTarget is the guard for every case where focus did NOT move
   * to somewhere else in the app, and it is load-bearing for the worst one:
   * ProseMirror DETACHES a widget's element on the whole-doc ReplaceStep that
   * every Yjs-origin change produces, and re-attaches the same cached shell
   * after. That transient detach blurs the focused island editor with no
   * incoming element, so dismissing on it would silently close an island out
   * from under a clinician who is still typing. The same null covers a window
   * blur / tab switch, which deliberately does NOT dismiss: the clinician has
   * not moved anywhere within the app and returns to the island as they left
   * it. Focus reaching a real element is the only thing that reads as leaving,
   * and a pointerdown on non-focusable chrome — the remaining way to leave
   * without focusing anything — is already the other trigger's job. */
  useEffect(() => {
    const wrapper = wrapperRef.current
    if (wrapper === null) {
      return
    }
    const onFocusOut = (event: FocusEvent): void => {
      const next = event.relatedTarget
      if (!(next instanceof Element) || staysOpenFor(next)) {
        return
      }
      dismiss()
    }
    wrapper.addEventListener('focusout', onFocusOut)
    return () => wrapper.removeEventListener('focusout', onFocusOut)
  }, [dismiss, staysOpenFor])

  return (
    <div
      ref={wrapperRef}
      /* No border and no padding of its own: the widget shell around it already
       * draws the card outline, and any box the island adds that the read-only
       * preview does not have is a jump in the note the moment the island opens.
       * The preview renders a bare EditorContent, so this must too — same text
       * origin, same card height, open or closed. */
      /* The green tint marks AI-proposed text. An island with no host actions
       * stands on a line the AI proposed nothing for — the clinician opened it
       * to type — so it wears none of the proposal's colour. */
      className={cn(
        'ai-proposal-island relative rounded-sm',
        actions && 'bg-green-50/60 dark:bg-green-950/30'
      )}
    >
      <EditorContent editor={editor} />
      {/* The three verbs exist only where there is a proposal to decide. A bare
       * island renders no toolbar whatsoever — not even a neutral close — so
       * that typing into a line the AI passed over is indistinguishable from
       * typing into an answer on a questionnaire it never touched. Leaving it is
       * the ordinary text-editing exit: a click elsewhere, or Tab, both of which
       * commit through the same dismiss path a toolbar button would have.
       *
       * Out of flow, floating above the card, exactly as the hover popover
       * floats above an unopened one — so opening an island reflows nothing
       * below it. Positioned by CSS against this wrapper rather than by
       * floating-ui: the toolbar is a DESCENDANT of the card it anchors to, so
       * the browser keeps the two aligned through every reflow, where a
       * computed position would have to be recomputed after each of the
       * whole-doc replacements that move the widget. Staying a descendant is
       * also what keeps `staysOpenFor`'s contains() test true (a portal would
       * dismiss the island the instant a button took focus) and what keeps the
       * buttons next in the Tab order after the island's text. */}
      {!!actions && (
        <div className="absolute bottom-full left-1/2 z-50 flex -translate-x-1/2 pb-1.5">
          <div
            role="toolbar"
            aria-label="AI proposal editing actions"
            /* Opaque popover chrome, matching AIProposalHoverPopover: the
             * toolbar now floats over the note's own text, which it has to stay
             * legible against. */
            className={cn(
              'flex items-center gap-1 rounded-md border bg-popover px-1 py-0.5 shadow-md',
              'text-popover-foreground text-xs'
            )}
          >
            <button
              type="button"
              className="flex items-center gap-1 rounded px-1.5 py-0.5 text-green-700 hover:bg-green-50 dark:text-green-300 dark:hover:bg-green-950"
              onClick={acceptWithEdits}
            >
              <IconCheck className="size-3.5" />
              Accept with edits
            </button>
            <button
              type="button"
              className="flex items-center gap-1 rounded px-1.5 py-0.5 text-muted-foreground hover:bg-accent disabled:opacity-50 disabled:hover:bg-transparent"
              disabled={!canRestore}
              onClick={restore}
            >
              <IconArrowBackUp className="size-3.5" />
              Restore original
            </button>
            <button
              type="button"
              className="flex items-center gap-1 rounded px-1.5 py-0.5 text-red-700 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-950"
              onClick={reject}
            >
              <IconX className="size-3.5" />
              Reject
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
